import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { ReviewVideoPlayer } from "./ReviewVideoPlayer";
import {
  exitLiveFullscreen,
  minimizeMobileBrowserChrome,
} from "../hooks/useLiveFullscreen";
import "./ReviewVideoFullscreenPlayer.css";

/**
 * Portal target for the review player.
 * Always prefer document.body so exercise-results sheets (also on body) cannot
 * cover the player. Only stay inside `.lt-page` when that page is already the
 * browser fullscreen element (live training).
 */
function getReviewVideoPortalRoot() {
  if (typeof document === "undefined") return null;
  const fs =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    null;
  if (fs?.classList?.contains("lt-page")) return fs;
  return document.body;
}

/**
 * Full-screen review video player (portal, z-index above session result sheets).
 * Always uses ReviewVideoPlayer so laptop trackpad zoom/pan and keyboard
 * transport shortcuts work on desktop as well as mobile.
 */
export function ReviewVideoFullscreenPlayer({
  url,
  onClose,
  title,
  autoPlay = true,
  canTransformVideo = false,
  messageId = null,
  initialViewSetup = null,
  saveViewSetup,
  onViewSetupSaved,
  onRequestReupload,
  hrPoints = null,
  hrSeries = null,
  sequenceTimestamp = 0,
  teId = null,
  tableId = null,
  enablePose = false,
  poseFps = null,
  sequenceOrder = null,
  exerciseTypeEn = "",
  exerciseSubtypeEn = "",
  exerciseNameEn = "",
  activePlayerId = null,
  sequenceMeta = null,
  reelOwners = null,
  onStrokeReelUploaded = null,
  isKotMatch = false,
  kotMatchId = null,
  isEvaluationVideo = false,
  isShareableReel = false,
  shareableReelKind = "max_rally",
  enableJointOverlay = false,
  jointOverlayAllPlayers = false,
  initialCurrentTime = null,
  playbackWindowStart = null,
  playbackWindowEnd = null,
  eagerPlayback = false,
  className = "",
}) {
  const { t } = useTranslation();
  const shellRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const openedAtRef = useRef(0);
  const open = Boolean(url);

  useEffect(() => {
    if (!open) return undefined;
    openedAtRef.current = Date.now();
    const root = document.documentElement;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const hadImmersive = root.classList.contains("lt-immersive");
    body.style.overflow = "hidden";
    root.classList.add("lt-immersive");
    body.classList.add("lt-immersive");

    const syncViewport = () => {
      const vv = window.visualViewport;
      const h = vv?.height || window.innerHeight;
      if (!h) return;
      root.style.setProperty("--lt-vvh", `${Math.round(h)}px`);
      root.style.setProperty("--lt-vvt", `${Math.round(vv?.offsetTop || 0)}px`);
      root.style.setProperty("--lt-vvl", `${Math.round(vv?.offsetLeft || 0)}px`);
    };
    syncViewport();
    minimizeMobileBrowserChrome();

    // Do not auto-request browser fullscreen here. The shell is already a fixed
    // viewport overlay; requesting FS often fails (iOS) or races Escape and
    // made the player dismiss itself as soon as a clip opened.

    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Ignore Escape for a short grace window after open (browser FS / focus quirks).
      if (Date.now() - openedAtRef.current < 600) return;
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("scroll", syncViewport);

    return () => {
      body.style.overflow = prevOverflow;
      if (!hadImmersive) {
        root.classList.remove("lt-immersive");
        body.classList.remove("lt-immersive");
      }
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("scroll", syncViewport);
      const current =
        document.fullscreenElement || document.webkitFullscreenElement || null;
      const shell = shellRef.current;
      if (current && (current === root || current === shell)) {
        exitLiveFullscreen();
      }
    };
  }, [open]);

  if (!url || typeof document === "undefined") return null;

  const portalRoot = getReviewVideoPortalRoot() || document.body;

  return createPortal(
    <div
      ref={shellRef}
      className={["review-video-fullscreen-player", className].filter(Boolean).join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label={title || t("watchVideo", "Watch video")}
    >
      <button
        type="button"
        className="review-video-fullscreen-player__close"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose?.();
        }}
        aria-label={t("close", "Close")}
      >
        <X size={22} strokeWidth={2.5} />
      </button>

      <ReviewVideoPlayer
        url={url}
        mode="fullscreen"
        autoPlay={autoPlay}
        canTransformVideo={canTransformVideo}
        messageId={messageId}
        initialViewSetup={initialViewSetup}
        saveViewSetup={saveViewSetup}
        onViewSetupSaved={onViewSetupSaved}
        onRequestReupload={onRequestReupload}
        hrPoints={hrPoints}
        hrSeries={hrSeries}
        sequenceTimestamp={sequenceTimestamp}
        teId={teId}
        tableId={tableId}
        enablePose={enablePose}
        poseFps={poseFps}
        sequenceOrder={sequenceOrder}
        exerciseTypeEn={exerciseTypeEn}
        exerciseSubtypeEn={exerciseSubtypeEn}
        exerciseNameEn={exerciseNameEn}
        activePlayerId={activePlayerId}
        sequenceMeta={sequenceMeta}
        reelOwners={reelOwners}
        onStrokeReelUploaded={onStrokeReelUploaded}
        isKotMatch={isKotMatch}
        kotMatchId={kotMatchId}
        isEvaluationVideo={isEvaluationVideo}
        isShareableReel={isShareableReel}
        shareableReelKind={shareableReelKind}
        enableJointOverlay={enableJointOverlay}
        jointOverlayAllPlayers={jointOverlayAllPlayers}
        initialCurrentTime={initialCurrentTime}
        playbackWindowStart={playbackWindowStart}
        playbackWindowEnd={playbackWindowEnd}
        eagerPlayback={eagerPlayback}
      />
    </div>,
    portalRoot
  );
}

export default ReviewVideoFullscreenPlayer;
